<script lang="ts">
import { createEventDispatcher } from 'svelte';
import deepEqual from 'fast-deep-equal';
import type { LectureListItem, Person, RefDto } from '../api/types';
import courses from '../store/courses';

import FileList from './FileList.svelte';
import PersonPicker from './PersonPicker.svelte';
import Input from './Input.svelte';
import Button from './Button.svelte';
import AddItemControl from './AddItemControl.svelte';
import PersonEditorOverlay from './PersonEditorOverlay.svelte';
import type { PersonEvent } from './PersonEditorOverlay.svelte';
import EmailPreview from './EmailPreview.svelte';
import type { EmailTemplate } from './EmailPreview.svelte';

export let lecture: LectureListItem;
export let courseId: string;
let form: LectureListItem;
let prev: LectureListItem;
let locked = false;
let updated = false;
let editPerson: Person | undefined;
const dispatch = createEventDispatcher();
const emptyPerson: Person = {
    id: '',
    description: '',
    name: '',
    files: []
};
const email: EmailTemplate[] = [
    ['Напоминание о начале лекции за 24 часа', 'reminder_24h'],
    ['Напоминание о начале лекции за 2 часа', 'reminder_2h'],
    ['Напоминание о начале лекции за 15 минут', 'reminder_15m'],
    ['Лекция завершена', 'lecture_finished'],
    ['Доступно видео лекции', 'lecture_video'],
]

$: clone(lecture);
$: updated = !deepEqual(form, lecture);

function clone(data: LectureListItem): void {
    // Обходим слишком умную систему Svelte построения зависимостей для инвалидации.
    // Логичнее было бы на уровне компонента написать
    // $: form = clone(course)
    // Однако в этом случае Svelte зачем-то добавит в биндингах инвалидацию
    // объекта `course`, что в итоге приведёт к новому циклу обновления `form`
    // и перетрёт данные.
    // В такой реализации инвалидация будет работать правильно
    if (prev !== data) {
        form = {
            ...data,
            lecturers: [...data.lecturers],
            files: []
        };
        if (!form.lecturers.length) {
            // Делаем заглушку, чтобы было что обновлять
            form.lecturers = [{ id: '' }];
        }
        prev = data;
    }
}

function onSubmit() {
    const payload = { ...form };
    payload.lecturers = payload.lecturers.filter(p => p.id);
    console.log('submit', payload, form);
    dispatch('submit', payload);
}

function onReset() {
    clone(lecture);
}

function onUploadEnd(evt: CustomEvent) {
    locked = false;
    form.files = evt.detail;
}

function onDelete(evt: CustomEvent) {
    form.files = evt.detail;
}

function onAddPerson(ix: number) {
    let { lecturers } = form;
    if (lecturers[ix]) {
        lecturers = [...lecturers];
        lecturers.splice(ix + 1, 0, { id: '' });
        form = { ...form, lecturers };
    }
}

function onRemovePerson(ix: number) {
    let { lecturers } = form;
    if (lecturers[ix]) {
        lecturers = [...lecturers];
        lecturers.splice(ix, 1);
        form = { ...form, lecturers };
    }
}

function onEditPerson(evt: PersonEvent) {
    editPerson = null;
    if (!evt.detail.isNew) {
        return;
    }

    let { lecturers } = form;
    const next: RefDto = { id: evt.detail.person.id };

    if (!lecturers.length) {
        lecturers = [next];
    } else {
        let lastPerson = lecturers[lecturers.length - 1];
        lecturers = [...lecturers];
        if (!lastPerson.id) {
            lecturers.pop();
        }

        lecturers.push(next);
    }

    form = { ...form, lecturers };
}

function onUpdatePerson(ix: number, id: string) {
    let { lecturers } = form;
    if (lecturers[ix]) {
        lecturers = [...lecturers];
        lecturers[ix] = { id };
        form = { ...form, lecturers };
    }
}
</script>
<style lang="scss">
form {
    > :global(.input) {
        margin-top: 24px;
    }
}

.persons {
    :global(.person-picker) {
        margin-top: 24px;
    }

    :global(.add-item-control) {
        margin-top: 30px;
    }
}
</style>

<form action="/" on:submit|preventDefault={onSubmit} on:reset|preventDefault={onReset}>
    <Input name="title" bind:value={form.title}>Название лекции</Input>
    <Input name="shortDescription" type="html" bind:value={form.shortDescription}>Краткое описание</Input>
    <Input name="description" type="html" bind:value={form.description}>Описание</Input>
    <Input name="dateStart" type="datetime" bind:value={form.dateStart}>Дата начала</Input>

    <h2>Лекторы</h2>
    <div class="persons">
        {#each form.lecturers as person, i}
            <PersonPicker selected={person.id} removable={form.lecturers.length > 1}
                on:edit={evt => editPerson = evt.detail}
                on:update={evt => onUpdatePerson(i, evt.detail)}
                on:add={() => onAddPerson(i)}
                on:remove={() => onRemovePerson(i)} />
        {/each}
        <AddItemControl on:click={() => editPerson = emptyPerson}>Добавить лектора</AddItemControl>
    </div>

    <h2>Обложки и файлы по лекции</h2>
    <FileList files={lecture.files}
        on:upload-start={() => locked = true}
        on:upload-end={onUploadEnd}
        on:delete={onDelete} />

    <EmailPreview items={email} {courseId} lectureId={lecture.id} />

    <footer>
        <Button type="reset">Сбросить</Button>
        <Button type="submit" role="primary" disabled={$courses.progress === 'lecture' ? 'progress' : (!locked && !updated)}>Сохранить данные</Button>
    </footer>
</form>

{#if editPerson}
    <PersonEditorOverlay person={editPerson}
        on:update={onEditPerson}
        on:close={() => editPerson = null} />
{/if}
